import React, { useState, useMemo, createContext, useContext, useEffect } from 'react';
import { HashRouter as Router, Route, Routes } from 'react-router-dom';
import {
  ThemeProvider,
  CssBaseline,
  PaletteMode,
  useMediaQuery
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import safeStorage from './utils/safeStorage';
import { createAppTheme } from './theme';

// Pages
import LoginPage from './pages/Public/LoginPage';
import BookAppointmentPage from './pages/Public/BookAppointmentPage';
import AppointmentsPage from './pages/Admin/AppointmentsPage';
import SchedulePage from './pages/Mechanic/SchedulePage';
import InvoicePage from './pages/Admin/InvoicePage';
import HomePage from './pages/Public/HomePage';
import UnauthorizedPage from './pages/Public/UnauthorizedPage';
import MechanicsPage from './pages/Admin/MechanicsPage';
import MechanicsCalendarPage from './pages/Admin/MechanicsCalendarPage';
import ShopsListPage from './pages/Public/ShopsListPage';
import MarketingPage from './pages/Public/MarketingPage';
import MechanixHubPage from './pages/MechanixHubPage';
import ShopSettingsPage from './pages/Admin/ShopSettingsPage';
import ShopsAdminPage from './pages/Admin/ShopsAdminPage';
import DashboardPage from './pages/Admin/DashboardPage';
import ServiceCatalogPage from './pages/Admin/ServiceCatalogPage';
import IntegrationsPage from './pages/Admin/IntegrationsPage';
import InventoryPage from './pages/Admin/InventoryPage';
import PurchaseOrdersPage from './pages/Admin/PurchaseOrdersPage';
import PlatformIntegrationsPage from './pages/Admin/PlatformIntegrationsPage';
import AppointmentTrackingPage from './pages/Public/AppointmentTrackingPage';
import PayInvoicePage from './pages/Public/PayInvoicePage';
import ForgotPasswordPage from './pages/Public/ForgotPasswordPage';
import ResetPasswordPage from './pages/Public/ResetPasswordPage';

// Components
import AppChrome from './components/AppChrome';
import RouteGuard from './components/RouteGuard';
import PageTransition from './components/PageTransition';

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { ShopProvider } from './contexts/ShopContext';

// Types
import { UserRole } from './types';

// Create a theme context
interface ThemeContextType {
  mode: PaletteMode;
  toggleColorMode: () => void;
}

export const ColorModeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleColorMode: () => {},
});

// Custom hook to use the theme context
export const useColorMode = () => useContext(ColorModeContext);

const App: React.FC = () => {
  // Use system preference as the default theme
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  
  // Check localStorage for saved preference, or use system preference
  const storedMode = safeStorage.getItem('colorMode') as PaletteMode | null;
  const [mode, setMode] = useState<PaletteMode>(
    storedMode || (prefersDarkMode ? 'dark' : 'light')
  );

  // Effect to save mode preference to localStorage
  useEffect(() => {
    safeStorage.setItem('colorMode', mode);
  }, [mode]);

  // Color mode toggle function
  const colorMode = useMemo(
    () => ({
      mode,
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    [mode]
  );

  // All styling decisions live in src/theme — see tokens.ts for the values.
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <AuthProvider>
            <SocketProvider>
              {/* Router should be the top-level routing component */}
              <Router>
                {/* ShopProvider needs to be INSIDE Router to use hooks like useNavigate */}
                <ShopProvider>
                  {/* Sidebar for admin screens, top bar everywhere else */}
                  <AppChrome>
                <Routes>
                  {/* Public routes */}
                      <Route 
                        path="/"
                        element={(
                          <RouteGuard requireShop>
                            <PageTransition><HomePage /></PageTransition>
                          </RouteGuard>
                        )}
                      />
                      <Route 
                        path="/login"
                        element={(
                          <RouteGuard requireShop> {/* Assuming login might be shop-specific */}
                            <PageTransition><LoginPage /></PageTransition>
                          </RouteGuard>
                        )}
                      />
                      <Route 
                        path="/book-appointment"
                        element={(
                          <RouteGuard requireShop>
                            <PageTransition><BookAppointmentPage /></PageTransition>
                          </RouteGuard>
                        )}
                      />
                      <Route 
                        path="/unauthorized"
                        element={(
                          <RouteGuard>
                            <PageTransition><UnauthorizedPage /></PageTransition>
                          </RouteGuard>
                        )}
                      />
                      <Route
                        path="/track/:id"
                        element={(
                          <PageTransition><AppointmentTrackingPage /></PageTransition>
                        )}
                      />
                      {/* Password recovery — no shop context, and reachable
                          while signed out */}
                      <Route
                        path="/forgot-password"
                        element={(
                          <PageTransition><ForgotPasswordPage /></PageTransition>
                        )}
                      />
                      <Route
                        path="/reset-password"
                        element={(
                          <PageTransition><ResetPasswordPage /></PageTransition>
                        )}
                      />
                      {/* Public pay page — customers arrive from the invoice email */}
                      <Route
                        path="/pay/:invoiceId"
                        element={(
                          <PageTransition><PayInvoicePage /></PageTransition>
                        )}
                      />
                      <Route
                        path="/marketing"
                        element={(
                          <RouteGuard allowAuthenticated>
                            <PageTransition><MarketingPage /></PageTransition>
                          </RouteGuard>
                        )}
                      />
                      {/* Shops list doesn't require a selected shop */}
                      <Route 
                        path="/shops"
                        element={(
                          <RouteGuard allowAuthenticated={true}>
                            <PageTransition><ShopsListPage /></PageTransition>
                          </RouteGuard>
                        )}
                      />
                      <Route 
                        path="/mechanix-hub"
                        element={(
                          <RouteGuard allowAuthenticated={true} requireShop={false}>
                            <PageTransition><MechanixHubPage /></PageTransition>
                          </RouteGuard>
                        )}
                      />

                      {/* Shop-specific routes (using URL slug) */}
                      {/* These implicitly require a shop via the ShopProvider triggered by useParams */}
                      {/* RouteGuard still used for auth/roles, not requireShop here as context handles slug */}
                      <Route 
                        path="/shop/:shopSlug"
                        element={(
                          <RouteGuard>
                            <PageTransition><HomePage /></PageTransition>
                          </RouteGuard>
                        )}
                      />
                      <Route 
                        path="/shop/:shopSlug/login"
                        element={(
                          <RouteGuard>
                            <PageTransition><LoginPage /></PageTransition>
                          </RouteGuard>
                        )}
                      />
                      <Route 
                        path="/shop/:shopSlug/book-appointment"
                        element={(
                          <RouteGuard>
                            <PageTransition><BookAppointmentPage /></PageTransition>
                          </RouteGuard>
                        )}
                      />

                      {/* Admin routes - Require Auth, Roles, and a selected Shop */}
                  <Route
                    path="/admin/dashboard"
                        element={(
                          <RouteGuard requireAuth roles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} requireShop>
                            <PageTransition><DashboardPage /></PageTransition>
                          </RouteGuard>
                        )}
                  />
                  <Route
                    path="/admin/appointments"
                        element={(
                          <RouteGuard requireAuth roles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} requireShop>
                            <PageTransition><AppointmentsPage /></PageTransition>
                      </RouteGuard>
                        )}
                  />
                  <Route
                    path="/admin/invoices"
                        element={(
                          <RouteGuard requireAuth roles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} requireShop>
                            <PageTransition><InvoicePage /></PageTransition>
                      </RouteGuard>
                        )}
                  />
                  <Route
                    path="/admin/mechanics"
                        element={(
                          <RouteGuard requireAuth roles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} requireShop>
                            <PageTransition><MechanicsPage /></PageTransition>
                      </RouteGuard>
                        )}
                  />
                  <Route
                    path="/admin/mechanics-calendar"
                        element={(
                          <RouteGuard requireAuth roles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} requireShop>
                            <PageTransition><MechanicsCalendarPage /></PageTransition>
                      </RouteGuard>
                        )}
                  />
                  <Route
                    path="/admin/services"
                        element={(
                          <RouteGuard requireAuth roles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} requireShop>
                            <PageTransition><ServiceCatalogPage /></PageTransition>
                          </RouteGuard>
                        )}
                  />
                  <Route
                    path="/admin/platform/integrations"
                        element={(
                          <RouteGuard requireAuth roles={[UserRole.SUPER_ADMIN]}>
                            <PageTransition><PlatformIntegrationsPage /></PageTransition>
                          </RouteGuard>
                        )}
                  />
                  <Route
                    path="/admin/inventory"
                        element={(
                          <RouteGuard requireAuth roles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} requireShop>
                            <PageTransition><InventoryPage /></PageTransition>
                          </RouteGuard>
                        )}
                  />
                  <Route
                    path="/admin/purchase-orders"
                        element={(
                          <RouteGuard requireAuth roles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} requireShop>
                            <PageTransition><PurchaseOrdersPage /></PageTransition>
                          </RouteGuard>
                        )}
                  />
                  <Route
                    path="/admin/integrations"
                        element={(
                          <RouteGuard requireAuth roles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} requireShop>
                            <PageTransition><IntegrationsPage /></PageTransition>
                          </RouteGuard>
                        )}
                  />
                  <Route
                    path="/admin/shop-settings"
                        element={(
                          <RouteGuard requireAuth roles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]} requireShop>
                            <PageTransition><ShopSettingsPage /></PageTransition>
                      </RouteGuard>
                        )}
                  />

                      {/* Platform-level: super admins are not tied to a shop,
                          so this route deliberately does not require one */}
                      <Route
                        path="/admin/shops"
                        element={(
                          <RouteGuard requireAuth roles={[UserRole.SUPER_ADMIN]}>
                            <PageTransition><ShopsAdminPage /></PageTransition>
                          </RouteGuard>
                        )}
                      />

                      {/* Mechanic routes - Require Auth, Roles, and a selected Shop */}
                  <Route
                    path="/mechanic/schedule"
                        element={(
                          <RouteGuard requireAuth roles={[UserRole.MECHANIC, UserRole.ADMIN, UserRole.SUPER_ADMIN]} requireShop>
                            <PageTransition><SchedulePage /></PageTransition>
                      </RouteGuard>
                        )}
                  />
                </Routes>
                  </AppChrome>
                </ShopProvider>
              </Router>
            </SocketProvider>
          </AuthProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default App;
